/* =============================================================================
   ics-tooltip - the hover/focus label of the own library: a small floating card
   that names or explains the control under the pointer, fed by the element's
   own `title` text (or a `content` option), themed by the token tiers and
   positioned in JS so it can never be clipped by an `overflow:hidden` ancestor
   - which is where a toolbar button lives on almost every screen.

   AN ADD-ON, NOT THE ELEMENT'S COMPONENT. `el[Component.KEY]` is ONE realm-wide
   slot per element (core/component.js - Symbol.for, class-blind idempotency),
   and 725 of the estate's tooltip carriers are BUTTONS whose slot the button
   component rightfully owns; a tooltip built on that base could never mount on
   them (whichever bundle's ready pass ran first would win the slot and the
   other component would silently die). So this module follows the library's
   add-on contract - the same one the autocomplete rides a text field with: its
   OWN realm-shared instance key (`Symbol.for('ics.tooltip.instance')`), its own
   EventBinder, its own facade - and therefore CO-EXISTS with whatever component
   owns the element. It deliberately does NOT extend Component; the small
   option()/emit()/destroy() surface is restated here against that own key.

   MOUNT. `[data-ics-tip]` (a valueless marker attribute - `data-ics` on these
   elements is already the component slot, and `data-ics-ext` is the text
   field's autocomplete slot, so the tooltip carries its own marker). The
   attribute may also CARRY the text (`data-ics-tip="Refreshes the list"`),
   which then wins over `title`. Registered in the Registry for AUTO-MOUNT ONLY
   (`facade:false` - the generated bridges resolve through Component.get, which
   on a button answers the button); the call name `$.fn.icsTooltip` is this
   module's own bridge (attachTooltipFacade), same calling convention as every
   ics name: options object = init-or-merge, method string = dispatch, getters
   (`tip` included) answer undefined on an absent/unmounted receiver, mutators
   keep chaining and run on every element of the set. NO other jQuery name is
   touched, read, wrapped or shadowed by this module - the library's NAME RULE
   (facade/register.js) lists the plain short name as one another script on the
   page already defines, and it stays that script's.

   THE TITLE CONTRACT. On mount the element's `title` is CACHED and BLANKED
   (`title=""`), so the browser's native bubble never doubles the card.
     - The text lives on: show() renders the cached text unless `content` /
       `data-ics-tip` overrides it.
     - A LATE `title` WRITE IS ADOPTED: every show() re-reads the attribute; a
       non-empty value found there (a screen wrote `.attr('title', ...)` after
       mount) becomes the new cached text and is blanked again - so runtime
       title updates keep working without an observer.
     - The accessible name is preserved, not lost: when blanking would leave an
       icon-only control nameless (no `aria-label`, no visible text), the
       cached text is written to `aria-label`; the module REMEMBERS that the
       label is its own, keeps it in step when a late title write is adopted,
       and removes it again on destroy() - the element leaves as authored.
     - destroy() also restores the cached text into `title` (only when nothing
       else has claimed the attribute meanwhile).

   ONE CARD PER PAGE. The card is a page singleton, shared by every instance
   and every bundle copy (realm key `Symbol.for('ics.tooltip')`): show()
   retargets it, so hovering along a toolbar re-uses one node instead of
   leaving a div behind per control ever hovered. A rebuild after the card was
   detached RESETS the card binder (removeAll + fresh) before re-binding, so
   rebuilds never accumulate listener records. While a card is visible its
   owner runs a light WATCHDOG (tracked interval, ~0.4s): a trigger that leaves
   the DOM mid-show (a list re-render under the pointer) fires no mouseleave,
   and the watchdog is what hides the card and releases the Escape handler
   instead of leaving a ghost on screen.

   KEYBOARD + WCAG 1.4.13 (content on hover or focus).
     - DISMISSIBLE: while the card shows, an Escape handler is registered with
       the central KeyService (services/windowstack.js - never a private
       document listener). On dismiss it CONTAINS the keypress fully:
       preventDefault + stopImmediatePropagation (the dispatcher runs in the
       document capture phase, so this stops the message box's own capture
       handler AND every page-level bubble handler - the estate is full of
       `$(document).keydown -> icsWindow('close')` blocks whose close path is
       exempt from the one-ESC guard) + WindowStackService.noteEscConsumed()
       as the belt for anything already dispatched. One Escape = the card
       hides, and NOTHING else answers that keypress. Registered on show,
       unregistered on hide/destroy - at rest this module holds no key handler.
     - HOVERABLE: the pointer may travel INTO the card - entering it cancels
       the pending hide (hideDelay grace), leaving it hides.
     - PERSISTENT: no auto-hide timer; the card stays until pointer/focus
       leaves, Escape, hide() or destroy().
     - Focus parity: focusin shows exactly as mouseenter does; the card itself
       is never focusable.
     - The card is `role="tooltip"` and the trigger carries
       `aria-describedby` -> the card id while visible (describedby, never
       labelledby: the tip SUPPLEMENTS the control's name).

   CONTENT SAFETY. Text by default - content renders through the Sanitizer's
   setContent (textContent path), so a title carrying markup shows it as
   characters. `html:true` is the explicit opt-in and routes through
   Sanitizer.sanitizeHtml. `update('')` (or `content:''`) means EXPLICITLY
   NOTHING - the card clears/hides and does NOT fall back to the cached title;
   only `null` means "read the title". Never raw innerHTML.

   POSITIONING. `position:fixed`, appended to document.body: immune to every
   overflow/scroll ancestor. Placed from getBoundingClientRect() on the side
   `position` asks for (top | bottom | left | right, default bottom); when that
   side does not fit the card flips to the opposite side, and when NEITHER
   fits it takes whichever side has more room - always into a local variable,
   options.position is never mutated - then clamps into the viewport. After a
   clamp the ARROW is re-aimed at the trigger's centre (inline offset), so an
   edge-of-viewport card still points at the control it labels. The z plane is
   CSS (`--ics-z-tooltip`): above the dropdown band, below overlay and toasts.

   API. options {content, position, showDelay, hideDelay, html, disabled,
   onShow, onHide} - show() · hide() · update(content) · tip() (the card
   element while this instance owns it, else null) · option() · destroy().
   An options-object call on an already-mounted trigger MERGES and re-renders.
   `disabled` gates show() through isOn - all three authored routes of a
   boolean read the same. Ships in the dialog-family bundle (its entry attaches
   `$.fn.icsTooltip` and re-asserts it in the ready queue) with its paint in
   lib-v2\css\components\ics-tooltip.css.
   ============================================================================= */
import { isOn, resolveOptions } from '../core/options.js';
import { Registry } from '../services/registry.js';
import { EventBinder } from '../core/event-binder.js';
import { KeyService, WindowStackService } from '../services/windowstack.js';
import { setContent, html as trustHtml, sanitizeHtml } from '../services/sanitizer.js';

/* ---- the page-singleton card ------------------------------------------------
   Realm-shared (Symbol.for) for the same reason the window stack is: a second
   bundle copy carrying this module must retarget THE card, not raise a twin -
   two cards under one pointer would fight over aria-describedby and Escape. */
const _shared = (() => {
  const K = Symbol.for('ics.tooltip');
  const g = globalThis;
  if (!g[K]) g[K] = { card: null, content: null, arrow: null, binder: null, owner: null };
  return g[K];
})();

/* THE INSTANCE KEY - the add-on's own slot on the element, so a Button, a
   Textbox or a Numberbox holding `Component.KEY` and this tooltip co-exist on
   one tag. Realm-shared so every bundle copy resolves the same instance. */
const KEY = Symbol.for('ics.tooltip.instance');

const CARD_ID = 'ics-tooltip-card';
const SIDES = ['top', 'bottom', 'left', 'right'];
const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const VIEW_PAD = 4;                                 // the card never touches the viewport edge
const WATCH_MS = 400;                               // visible-card watchdog cadence

/** build (once) and return the shared card. A rebuild resets the card binder
    first, so repeated rebuilds never accumulate listener records. */
function card() {
  if (_shared.card && _shared.card.isConnected) return _shared.card;
  const el = document.createElement('div');
  el.className = 'ics-tooltip';
  el.id = CARD_ID;
  el.setAttribute('role', 'tooltip');
  el.hidden = true;
  const content = document.createElement('div');
  content.className = 'ics-tooltip__content';
  const arrow = document.createElement('div');
  arrow.className = 'ics-tooltip__arrow';
  el.append(content, arrow);
  document.body.appendChild(el);
  _shared.card = el; _shared.content = content; _shared.arrow = arrow;
  if (_shared.binder) _shared.binder.removeAll();   // a detached card's records go with it
  _shared.binder = new EventBinder(el);
  /* HOVERABLE: entering the card holds it open; leaving it hides through the
     owner, so the owner's grace timer and events stay the single hide path */
  _shared.binder.add(el, 'mouseenter', () => { const o = _shared.owner; if (o) o._holdOpen(); });
  _shared.binder.add(el, 'mouseleave', () => { const o = _shared.owner; if (o) o._queueHide(); });
  return el;
}

export class Tooltip {
  static defaults = {
    content: null,          // the text; null = the element's own (cached) title; '' = explicitly nothing
    position: 'bottom',     // top | bottom | left | right - the ASKED side; a flip never writes back
    showDelay: 200,         // ms from pointer-enter/focus to show
    hideDelay: 100,         // ms grace after pointer-leave - the corridor into the card
    html: false,            // explicit opt-in: content is allowlist-sanitized markup instead of text
    disabled: false,        // gate on show(); read through isOn
    onShow: null,           // fired as 'ics:show' + callback when the card appears for this trigger
    onHide: null,           // fired as 'ics:hide' + callback when it leaves this trigger
  };

  constructor(el, options) {
    const existing = el[KEY];
    if (existing) return existing;                  // idempotent on the ADD-ON's own slot
    this.el = el;
    this.options = resolveOptions(Tooltip, el, options);
    this._events = new EventBinder(el);
    this._disposed = false;
    el[KEY] = this;
    this._init();
  }

  /** resolve the add-on instance on an element (the facade resolves through this) */
  static get(el) { return (el && el[KEY]) || null; }

  _init() {
    const el = this.el;
    this._title = '';
    this._visible = false;
    this._showTimer = null;
    this._hideTimer = null;
    this._watchTimer = null;
    this._unregKey = null;
    this._describedPrior = null;
    this._ariaVal = null;                            // the aria-label THIS module wrote, if any

    /* the marker attribute may carry the text; an authored value beats title */
    if (this.options.content == null && el.dataset.icsTip) this.options.content = el.dataset.icsTip;

    this._adoptTitle();

    /* accessible name: blanking title must not leave an icon-only control
       nameless. Written only when nothing else names the element; remembered
       so a late title adoption keeps it current and destroy() removes it. */
    if (this._title && !el.hasAttribute('aria-label') && !(el.textContent || '').trim()) {
      el.setAttribute('aria-label', this._title);
      this._ariaVal = this._title;
    }

    this._events.add(el, 'mouseenter', () => this._queueShow());
    this._events.add(el, 'focusin', () => this._queueShow());
    this._events.add(el, 'mouseleave', () => this._queueHide());
    this._events.add(el, 'focusout', () => this.hide());     // focus left: no corridor to walk, hide now
  }

  /* cache + blank the element's title; called at mount and re-run by every
     show(), so a `.attr('title', ...)` written after mount is ADOPTED instead
     of re-arming the native bubble beside the card. An adoption also refreshes
     the module's OWN aria-label so screen readers track the visible text. */
  _adoptTitle() {
    const t = this.el.getAttribute('title');
    if (t) {
      this._title = t;
      this.el.setAttribute('title', '');
      if (this._ariaVal != null && this.el.getAttribute('aria-label') === this._ariaVal) {
        this.el.setAttribute('aria-label', t);
        this._ariaVal = t;
      }
    }
  }

  /** the text the card would show right now ('' = nothing to show).
      `content` null falls back to the cached title; '' is EXPLICITLY nothing. */
  _text() {
    this._adoptTitle();
    const c = this.options.content;
    return c == null ? this._title : String(c);
  }

  _queueShow() {
    if (isOn(this.options.disabled) || this._disposed) return;
    this._events.clearTimer(this._hideTimer);
    if (this._shown()) return;
    this._events.clearTimer(this._showTimer);
    this._showTimer = this._events.timeout(() => this.show(), this.options.showDelay | 0);
  }

  _queueHide() {
    this._events.clearTimer(this._showTimer);
    if (!this._shown()) return;
    this._events.clearTimer(this._hideTimer);
    this._hideTimer = this._events.timeout(() => this.hide(), this.options.hideDelay | 0);
  }

  _holdOpen() { this._events.clearTimer(this._hideTimer); }

  _shown() { return this._visible && _shared.owner === this; }

  /** show the card for this trigger now (also the manual path; a queued show lands here) */
  show() {
    if (isOn(this.options.disabled) || this._disposed) return this;
    if (!this.el.isConnected || typeof document === 'undefined' || !document.body) return this;
    const text = this._text();
    if (!text) { if (this._shown()) this.hide(); return this; }   // nothing to say - never an empty card
    const prev = _shared.owner;
    if (prev && prev !== this) prev.hide();                  // one card, one owner
    const wasVisible = this._shown();                        // an update() re-show renders + places only
    const c = card();
    if (isOn(this.options.html)) setContent(_shared.content, trustHtml(sanitizeHtml(text)));
    else setContent(_shared.content, text);
    _shared.owner = this;
    this._visible = true;
    c.hidden = false;
    this._place(c);
    if (wasVisible) return this;
    /* DISMISSIBLE - and CONTAINED. The dispatcher runs at document CAPTURE, so
       stopImmediatePropagation() here stops the message box's own capture
       handler and every page-level `$(document).keydown` bubble handler alike;
       noteEscConsumed() arms the one-ESC guard for anything already mid-walk.
       One Escape = the card hides and nothing else on the page answers it.
       Registered only while visible; KeyService.count() stays 0 at rest. */
    if (!this._unregKey) {
      this._unregKey = KeyService.register((e) => {
        if (!this._shown()) return false;
        if (typeof e.preventDefault === 'function') e.preventDefault();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        else if (typeof e.stopPropagation === 'function') e.stopPropagation();
        WindowStackService.noteEscConsumed();
        this.hide();
        return true;
      }, { keys: ['Escape'] });
    }
    /* the visible-card watchdog: a trigger removed mid-show fires no
       mouseleave - this is the path that hides the ghost and frees Escape */
    if (!this._watchTimer) {
      this._watchTimer = this._events.interval(() => {
        if (this._shown() && !this.el.isConnected) this.hide();
      }, WATCH_MS);
    }
    const prior = this.el.getAttribute('aria-describedby');
    this._describedPrior = prior;
    this.el.setAttribute('aria-describedby', prior ? prior + ' ' + CARD_ID : CARD_ID);
    this.emit('show', { content: text });
    return this;
  }

  /** hide the card if this trigger owns it; releases Escape + watchdog + aria.
      A hide with nothing showing (a focusout before any show) is a silent no-op. */
  hide() {
    this._events.clearTimer(this._showTimer);
    this._events.clearTimer(this._hideTimer);
    if (!this._visible) return this;
    this._visible = false;
    if (this._unregKey) { this._unregKey(); this._unregKey = null; }
    if (this._watchTimer) { this._events.clearTimer(this._watchTimer); this._watchTimer = null; }
    if (_shared.owner === this) {
      _shared.owner = null;
      if (_shared.card) _shared.card.hidden = true;
    }
    /* restore aria-describedby exactly as found (ours removed, any prior kept) */
    if (this._describedPrior) this.el.setAttribute('aria-describedby', this._describedPrior);
    else this.el.removeAttribute('aria-describedby');
    this._describedPrior = null;
    this.emit('hide', {});
    return this;
  }

  /** replace the text; re-renders + re-places in place when the card is up.
      update('') clears - the card hides and does NOT fall back to the title. */
  update(content) {
    this.options.content = content == null ? null : String(content);
    if (this._shown()) this.show();
    return this;
  }

  /** the card element while THIS trigger owns it (a styling/measure anchor), else null */
  tip() { return this._shown() ? _shared.card : null; }

  /**
   * Read or patch options at runtime - the facade's merge path lands here.
   *   option('name') / option('name', value) / option({a:1, b:2})
   */
  option(name, value) {
    if (typeof name === 'object') {
      for (const [k, v] of Object.entries(name)) this.option(k, v);
      return this;
    }
    if (arguments.length === 1) return this.options[name];
    const old = this.options[name];
    this.options[name] = value;
    this._optionChanged(name, value, old);
    return this;
  }

  _optionChanged(name, value) {
    if (name === 'disabled' && isOn(value)) this.hide();
    else if (name === 'content' && this._shown()) this.update(value);
  }

  /** namespaced event + matching option callback (the library's uniform shape) */
  emit(type, detail) {
    if (this._disposed) return true;
    const ok = this.el.dispatchEvent(new CustomEvent('ics:' + type, {
      detail, bubbles: true, cancelable: true,
    }));
    const cb = this.options['on' + type.charAt(0).toUpperCase() + type.slice(1)];
    if (typeof cb === 'function') cb.call(this, detail);
    return ok;
  }

  /* ---- placement: fixed, viewport-space, flip / best-fit + clamp ------------ */
  _place(c) {
    const gap = this._gap(c);
    const r = this.el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    /* measure invisibly at natural size before choosing a side */
    c.style.visibility = 'hidden';
    c.style.left = '0px'; c.style.top = '0px';
    const b = c.getBoundingClientRect();
    let side = SIDES.indexOf(this.options.position) >= 0 ? this.options.position : 'bottom';
    const room = {
      top: r.top - VIEW_PAD, bottom: vh - r.bottom - VIEW_PAD,
      left: r.left - VIEW_PAD, right: vw - r.right - VIEW_PAD,
    };
    const need = (s) => (s === 'top' || s === 'bottom') ? gap + b.height : gap + b.width;
    if (room[side] < need(side)) {
      /* flip when the opposite fits; when NEITHER fits, take the roomier side
         - always a LOCAL choice, options.position is never written */
      if (room[OPPOSITE[side]] >= need(OPPOSITE[side]) || room[OPPOSITE[side]] > room[side]) side = OPPOSITE[side];
    }
    let left, top;
    if (side === 'top' || side === 'bottom') {
      left = r.left + r.width / 2 - b.width / 2;
      top = side === 'top' ? r.top - gap - b.height : r.bottom + gap;
    } else {
      top = r.top + r.height / 2 - b.height / 2;
      left = side === 'left' ? r.left - gap - b.width : r.right + gap;
    }
    left = Math.max(VIEW_PAD, Math.min(left, vw - b.width - VIEW_PAD));
    top = Math.max(VIEW_PAD, Math.min(top, vh - b.height - VIEW_PAD));
    for (const s of SIDES) c.classList.remove('ics-tooltip--' + s);
    c.classList.add('ics-tooltip--' + side);
    c.style.left = Math.round(left) + 'px';
    c.style.top = Math.round(top) + 'px';
    /* re-aim the arrow at the trigger after any clamp shift: the sheet centres
       it on the card; an inline offset points it back at the control */
    const a = _shared.arrow;
    if (a) {
      a.style.left = ''; a.style.top = ''; a.style.marginLeft = ''; a.style.marginTop = '';
      if (side === 'top' || side === 'bottom') {
        const ax = Math.max(6, Math.min(r.left + r.width / 2 - left, b.width - 6));
        a.style.left = Math.round(ax) + 'px'; a.style.marginLeft = '-4px';
      } else {
        const ay = Math.max(6, Math.min(r.top + r.height / 2 - top, b.height - 6));
        a.style.top = Math.round(ay) + 'px'; a.style.marginTop = '-4px';
      }
    }
    c.style.visibility = '';
  }

  /** trigger-to-card distance: the sheet's geometry knob, read live so a
      density retune reaches JS and CSS in one edit */
  _gap(c) {
    const v = parseFloat(getComputedStyle(c).getPropertyValue('--ics-tooltip-gap'));
    return Number.isFinite(v) ? v : 10;
  }

  /** idempotent teardown: card released, keys freed, element left as authored */
  destroy() {
    if (this._disposed) return this;
    this.hide();
    this._disposed = true;
    /* the cached text goes back into `title` unless some other writer has
       meanwhile put its own value there; the module's own aria-label leaves
       with it (only when still exactly what this module last wrote) */
    if (this._title && !this.el.getAttribute('title')) this.el.setAttribute('title', this._title);
    if (this._ariaVal != null && this.el.getAttribute('aria-label') === this._ariaVal) {
      this.el.removeAttribute('aria-label');
    }
    this._events.removeAll();
    delete this.el[KEY];
    return this;
  }
}

/**
 * The add-on's own jQuery bridge - `$.fn.icsTooltip`, and ONLY that name.
 * Same calling convention as the generated facades: an options object inits a
 * fresh trigger and MERGES on a mounted one; a method string dispatches on the
 * instance (mount-on-demand for declared `[data-ics-tip]` markup); a GETTER -
 * `tip`, `option('name')`, any `get*`/`is*` - answers undefined on an absent or
 * unmounted receiver, and a mutator keeps jQuery chaining and runs on every
 * element of the set. Restated here (rather than generated) because the
 * generated bridges resolve instances through the element's COMPONENT slot,
 * which on these triggers belongs to the button or the field.
 */
export function attachTooltipFacade($) {
  if (!$ || !$.fn) return false;
  const GETTER = /^(get|is|tip$|option$)/;
  $.fn.icsTooltip = function (arg, ...rest) {
    if (typeof arg === 'string') {
      if (!this.length) return GETTER.test(arg) ? undefined : this;   // absent element: silent no-op
      const resolve = (el) => {
        let i = Tooltip.get(el);
        if (!i && el.matches && el.matches('[data-ics-tip]')) i = new Tooltip(el);
        return i;
      };
      const inst = resolve(this[0]);
      if (!inst) {
        console.warn('[ics] $.fn.icsTooltip: \'' + arg + '\' called on unmounted element', this[0]);
        return GETTER.test(arg) ? undefined : this;
      }
      const member = inst[arg];
      if (typeof member !== 'function') {
        if (member !== undefined) return member;
        const err = new Error('[ics] $.fn.icsTooltip: unknown method \'' + arg + '\'');
        err.icsUnknownMethod = arg;
        err.icsComponent = 'Tooltip';
        throw err;
      }
      const out = member.apply(inst, rest);
      if (out !== inst) return out;                          // getter: first element only
      for (let i = 1; i < this.length; i++) {                // mutator: the whole set
        const other = resolve(this[i]);
        if (!other) { console.warn('[ics] $.fn.icsTooltip: \'' + arg + '\' called on unmounted element', this[i]); continue; }
        const m = other[arg];
        if (typeof m === 'function') m.apply(other, rest);
      }
      return this;
    }
    return this.each((_, el) => {
      const inst = Tooltip.get(el);
      if (inst) inst.option(arg || {});                      // mounted: merge + re-render
      else new Tooltip(el, arg || {});
    });
  };
  return true;
}

/* auto-mount only: the Registry pass constructs on `[data-ics-tip]` (the
   constructor is idempotent on the add-on's own key). facade:false - the
   generated bridge machinery must never answer for this module. */
Registry.add('Tooltip', Tooltip, { selector: '[data-ics-tip]', facade: false });
